<script setup lang="ts">
import RlDetailsGrid from '@/components/common/RlDetailsGrid.vue'
import StatusBadge from '@/components/common/StatusBadge.vue'
import ContainerDialog from '@/components/rc/containers/ContainerDialog.vue'
import ScanMinersDialog from '@/components/rc/containers/ScanMinersDialog.vue'
import { useContainers } from '@/composables/rc/containers'
import DetailsViewLayout from '@/views/layouts/DetailsViewLayout.vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ShipmentType } from '@/types/rc/shipments'
import { Icon } from '@iconify/vue'

const router = useRouter()

const props = defineProps<{ containerID: number }>()
const containerID = ref<number>(props.containerID)

const { containerDetailsQuery } = useContainers()
const { data, isFetching, isError, error, isRefetching, refetch } = containerDetailsQuery(
  containerID,
  computed(() => true),
  computed(() => true)
)

const containerDialogOpen = ref(false)
const scanMinersDialogOpen = ref(false)
const containerIDBeingUpdated = ref<number>()
const loading = computed(() => {
  return isFetching.value || isRefetching.value
})

const details = computed(() => {
  return [
    {
      label: 'Work Order',
      value: data.value?.work_order_number,
    },
    {
      label: 'Shipment Type',
      value: data.value?.kind,
    },
    {
      label: 'Status',
      value: data.value?.status,
    },
  ]
})

const containerDimensionDetails = computed(() => {
  return [
    {
      label: 'Type',
      value: data.value?.type,
    },
    {
      label: 'Dimensions (in)',
      value: `${data.value?.length ?? 0} x ${data.value?.width ?? 0} x ${data.value?.height ?? 0}`,
    },
    {
      label: 'Weight (lb)',
      value: `${data.value?.weight ?? 'Not specified'}`,
    },
  ]
})

const shipment = computed(() => data.value?.shipment)
const shipmentDetails = computed(() => {
  return [
    {
      label: 'ID',
      value: shipment.value?.id,
    },
    {
      label: 'Type',
      value: data.value?.kind,
    },
    {
      label: 'Status',
      value: shipment.value?.status,
    },
    {
      label: 'Carrier',
      value: shipment.value?.carrier,
    },
    {
      label: 'Tracking number',
      value: shipment.value?.tracking_number,
    },
    {
      label: 'BOL number',
      value: shipment.value?.bol_number,
    },
    {
      label: 'Waybill number',
      value: shipment.value?.waybill_number,
    },
  ]
})

const handleContainerEdit = () => {
  containerIDBeingUpdated.value = data?.value?.id
  containerDialogOpen.value = true
}

const handleAddMiners = () => {
  scanMinersDialogOpen.value = true
}
</script>

<template>
  <DetailsViewLayout
    back-button-text="Back to previous screen"
    :title="`Container ${data?.number}`"
    @on-back-click="router.back()"
  >
    <template #controls>
      <el-button v-if="data?.kind === ShipmentType.Inbound" type="primary" @click="handleAddMiners"
        >Add Miners</el-button
      >
      <el-button type="primary" @click="handleContainerEdit">Edit</el-button>
    </template>
    <rl-error-result v-if="isError" :error="error">
      <el-button @click="refetch()">Reload</el-button>
    </rl-error-result>
    <template v-else>
      <RlDetailsGrid class="w-full max-w-[1920px]" :items="details" :loading="loading" :columns="details.length">
        <template #scope="{ label, value }">
          <StatusBadge v-if="label.toLowerCase() === 'status' && value" class="w-fit" :status="value" />
          <RouterLink
            v-else-if="label === 'Work Order'"
            :to="{
              name: 'rc-work-order-dashboard',
              params: { number: value },
            }"
          >
            <el-button link>
              <div class="inline-flex gap-2 items-center">
                {{ value }}
                <Icon icon="material-symbols:open-in-new" :height="14" />
              </div>
            </el-button>
          </RouterLink>
          <p v-else>{{ value }}</p>
        </template>
      </RlDetailsGrid>
      <RlDetailsGrid
        class="w-full max-w-[1920px]"
        :items="containerDimensionDetails"
        :loading="loading"
        :columns="details.length"
      >
        <template #scope="{ value }">
          <p>{{ value }}</p>
        </template>
      </RlDetailsGrid>
      <el-divider style="margin: 0" />
      <div class="grid lg:grid-cols-[auto_auto_340px] gap-6 max-w-[1920px]">
        <MinersTableCard
          v-if="data"
          class="h-fit flex-1 grid-col-span-3 lg:grid-col-span-2 lg:grid-row-span-2 gap-6"
          :container="data"
          :loading="loading"
        />
        <div class="grid sm:grid-cols-subgrid col-span-3 lg:col-span-1 gap-6">
          <rl-details-card class="h-fit" title="Shipment" :items="shipmentDetails" :loading="loading" />
          <DamagesCard class="col-span-2 lg:grid-col-span-1" :damages="data?.damages" :loading="loading" />
        </div>
      </div>
    </template>
    <ContainerDialog v-model="containerDialogOpen" :container-id="containerIDBeingUpdated" />
    <ScanMinersDialog v-model="scanMinersDialogOpen" :container-id="data?.id!" />
  </DetailsViewLayout>
</template>
